import React, { useState, useEffect, FormEvent } from 'react';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Card, Button } from './ui';
import './UserManagement.css';

// Define User interface
interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  phoneNumber?: string;
  roles: string[];
  is_active: boolean;
  job_title?: string;
  invited_at?: string;
  activated_at?: string;
  invitation_expires?: string;
  last_login?: string;
  mfa_enabled?: boolean;
  mfa_required?: boolean;
  exclude_from_productivity?: boolean;
  taskAssignable?: boolean;
  assignableName?: string;
}

const UserManagement: React.FC = () => {
  const { apiRequest } = useAuth();

  // State for User Management
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
  const [userManagementMessage, setUserManagementMessage] = useState<string>('');

  // State for Add User Modal
  const [showAddUserModal, setShowAddUserModal] = useState<boolean>(false);
  const [newUserEmail, setNewUserEmail] = useState<string>('');
  const [newUserPassword, setNewUserPassword] = useState<string>('');
  const [newUserFirstName, setNewUserFirstName] = useState<string>('');
  const [newUserLastName, setNewUserLastName] = useState<string>('');
  const [newUserRoles, setNewUserRoles] = useState<string[]>(['user']);
  const [addUserModalMessage, setAddUserModalMessage] = useState<string>('');

  // State for Edit User Modal
  const [showEditUserModal, setShowEditUserModal] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editUserFirstName, setEditUserFirstName] = useState<string>('');
  const [editUserLastName, setEditUserLastName] = useState<string>('');
  const [editUserTitle, setEditUserTitle] = useState<string>('');
  const [editUserPhoneNumber, setEditUserPhoneNumber] = useState<string>('');
  const [editUserRoles, setEditUserRoles] = useState<string[]>(['user']);
  const [editUserPassword, setEditUserPassword] = useState<string>('');
  const [editUserExcludeFromProductivity, setEditUserExcludeFromProductivity] = useState<boolean>(false);
  const [editUserTaskAssignable, setEditUserTaskAssignable] = useState<boolean>(false);
  const [editUserAssignableName, setEditUserAssignableName] = useState<string>('');
  // Defaults TRUE to match the model: absent on the user record means enabled.
  const [editUserModalMessage, setEditUserModalMessage] = useState<string>('');

  // State for Delete User Modal
  const [showDeleteUserModal, setShowDeleteUserModal] = useState<boolean>(false);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [deleteUserModalMessage, setDeleteUserModalMessage] = useState<string>('');

  const ROLE_OPTIONS: Array<{ key: string; label: string; description?: string; locked?: boolean }> = [
    { key: 'user', label: 'User', description: 'Core workflow access', locked: true },
    { key: 'lender-manager', label: 'Lender Manager', description: 'Manage lenders & suggestions' },
    { key: 'payments-manager', label: 'Payments Manager', description: 'Access to payments section only' },
    { key: 'corpus-curator', label: 'Corpus Curator', description: 'Label CAF email clusters / overrides for the AI — no other admin access' },
    { key: 'workflow-publisher', label: 'Workflow Publisher', description: 'Publish and activate reviewed CAF workflows — no unrelated admin access' },
    { key: 'archive-viewer', label: 'Archive Viewer', description: 'Access to the Archived Tasks section only' },
    { key: 'credit-agreement-editor', label: 'Credit Agreement Editor', description: 'Edit credit-agreement details on claims (audited)' },
    { key: 'admin', label: 'Admin', description: 'Full administrator access' },
  ];

  const formatRoleName = (role: string): string => {
    const roleMap: Record<string, string> = {
      'admin': 'Admin',
      'lender-manager': 'Lender Manager',
      'payments-manager': 'Payments Manager',
      'corpus-curator': 'Corpus Curator',
      'workflow-publisher': 'Workflow Publisher',
      'archive-viewer': 'Archive Viewer',
      'credit-agreement-editor': 'Credit Agreement Editor',
      'user': 'User',
    };
    return roleMap[role] || role;
  };

  const ensureBaseUserRole = (roles: string[]): string[] => {
    const unique = Array.from(new Set([...roles, 'user']));
    return unique;
  };

  const toggleRole = (roles: string[], role: string): string[] => {
    const normalized = ensureBaseUserRole(roles);
    if (role === 'user') {
      return normalized;
    }

    const roleSet = new Set(normalized);
    if (roleSet.has(role)) {
      roleSet.delete(role);
    } else {
      roleSet.add(role);
    }

    return Array.from(roleSet);
  };

  const formatRoleList = (roles: string[]): string => {
    return ensureBaseUserRole(roles).map(formatRoleName).join(', ');
  };

  const handleToggleNewRole = (role: string) => {
    setNewUserRoles(prev => toggleRole(prev, role));
  };

  const handleToggleEditRole = (role: string) => {
    setEditUserRoles(prev => toggleRole(prev, role));
  };

  useEffect(() => {
    fetchUsers(); // Fetch users on mount
  }, [apiRequest]);

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    setUserManagementMessage('');
    try {
      const response = await apiRequest<User[]>({ // Expect an array of User
        url: '/ui/admin/users',
        method: 'GET',
      });
      setUsers(response || []); // Ensure response is not null/undefined
      if (!response || response.length === 0) {
        setUserManagementMessage('No users found or unable to fetch users.');
      }
    } catch (error: any) {
      console.error('Error fetching users:', error);
      const detail = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to fetch users.';
      setUserManagementMessage(`Error: ${detail}`);
      setUsers([]); // Clear users on error
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleOpenAddUserModal = () => {
    setNewUserEmail('');
    setNewUserPassword('');
    setNewUserFirstName('');
    setNewUserLastName('');
    setNewUserRoles(['user']);
    setAddUserModalMessage('');
    setShowAddUserModal(true);
  };

  const handleCloseAddUserModal = () => {
    setShowAddUserModal(false);
    setNewUserEmail('');
    setNewUserFirstName('');
    setNewUserLastName('');
    setNewUserRoles(['user']);
    setAddUserModalMessage('');
  };

  const handleSubmitAddUser = async (event: FormEvent) => {
    event.preventDefault();
    setAddUserModalMessage('Processing...');

    if (!newUserEmail) {
      setAddUserModalMessage('Email is required.');
      return;
    }

    if (newUserRoles.length === 0) {
      setAddUserModalMessage('Please select a role.');
      return;
    }

    try {
      await apiRequest({
        url: '/ui/admin/users',
        method: 'POST',
        data: {
          email: newUserEmail,
          firstName: newUserFirstName,
          lastName: newUserLastName,
          roles: ensureBaseUserRole(newUserRoles),
        },
      });
      setUserManagementMessage('User invited successfully! An invitation email has been sent.');
      fetchUsers(); // Refresh user list
      handleCloseAddUserModal();
    } catch (error: any) {
      console.error('Error inviting user:', error);
      const detail = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to invite user.';
      setAddUserModalMessage(`Error: ${detail}`);
    }
  };

  const handleAddUser = () => {
    // Placeholder for Add User functionality
    setUserManagementMessage('Add User functionality to be implemented.');
  };

  const handleResendInvitation = async (userId: string) => {
    console.log('Resending invitation for user:', userId);
    setUserManagementMessage('Resending invitation...');

    try {
      const response = await apiRequest({
        url: `/ui/admin/users/${userId}/resend-invitation`,
        method: 'POST',
      });
      console.log('Resend invitation response:', response);
      setUserManagementMessage('Invitation resent successfully!');
      fetchUsers(); // Refresh user list
    } catch (error: any) {
      console.error('Error resending invitation - Full error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      const detail = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to resend invitation.';
      setUserManagementMessage(`Error: ${detail}`);
    }
  };

  const handleEditUser = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) {
      setUserManagementMessage(`User ${userId} not found.`);
      return;
    }

    setEditingUser(user);
    setEditUserFirstName(user.firstName || '');
    setEditUserLastName(user.lastName || '');
    setEditUserTitle(user.title || '');
    setEditUserPhoneNumber(user.phoneNumber || '');
    setEditUserRoles(ensureBaseUserRole(user.roles));
    setEditUserExcludeFromProductivity(user.exclude_from_productivity || false);
    setEditUserTaskAssignable(user.taskAssignable || false);
    setEditUserAssignableName(user.assignableName || '');
    // `=== true`: undefined means never explicitly saved, which defaults to
    // disabled (maintainer decision 2026-08-26).
    setEditUserModalMessage('');
    setShowEditUserModal(true);
  };

  const handleCloseEditUserModal = () => {
    setShowEditUserModal(false);
    setEditingUser(null);
  };

  const handleSubmitEditUser = async (event: FormEvent) => {
    event.preventDefault();
    setEditUserModalMessage('Processing...');

    if (!editingUser) {
      setEditUserModalMessage('No user selected for editing.');
      return;
    }

    if (editUserRoles.length === 0) {
      setEditUserModalMessage('Please select a role.');
      return;
    }

    try {
      const updateData: any = {
        firstName: editUserFirstName,
        lastName: editUserLastName,
        title: editUserTitle,
        phoneNumber: editUserPhoneNumber,
        roles: ensureBaseUserRole(editUserRoles),
        exclude_from_productivity: editUserExcludeFromProductivity,
        taskAssignable: editUserTaskAssignable,
        assignableName: editUserAssignableName,
      };

      await apiRequest({
        url: `/ui/admin/users/${editingUser.id}`,
        method: 'PUT',
        data: updateData,
      });

      setUserManagementMessage('User updated successfully!');
      fetchUsers(); // Refresh user list
      // An admin unticking their OWN "Pinned open task" box should see the
      // lanes change now, not after a reload. Unconditional and cheap: editing
      // somebody else just re-reads the admin's own unchanged value.
      // Deliberately not awaited — a failed refresh must not turn a successful
      // save into an error toast, and the fetch falls back to enabled anyway.
      handleCloseEditUserModal();
    } catch (error: any) {
      console.error('Error updating user:', error);
      const detail = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to update user.';
      setEditUserModalMessage(`Error: ${detail}`);
    }
  };

  const handleDeleteUser = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) {
      setUserManagementMessage(`User ${userId} not found.`);
      return;
    }

    setDeletingUser(user);
    setDeleteUserModalMessage('');
    setShowDeleteUserModal(true);
  };

  const handleCloseDeleteUserModal = () => {
    setShowDeleteUserModal(false);
    setDeletingUser(null);
    setDeleteUserModalMessage('');
  };

  const handleConfirmDeleteUser = async () => {
    if (!deletingUser) {
      setDeleteUserModalMessage('No user selected for deletion.');
      return;
    }

    setDeleteUserModalMessage('Deleting user...');

    try {
      await apiRequest({
        url: `/ui/admin/users/${deletingUser.id}`,
        method: 'DELETE',
      });

      setUserManagementMessage('User deleted successfully!');
      fetchUsers(); // Refresh user list
      handleCloseDeleteUserModal();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      const detail = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to delete user.';
      setDeleteUserModalMessage(`Error: ${detail}`);
    }
  };

  const handleToggleMFARequirement = async (userId: string, require: boolean) => {
    setUserManagementMessage('Updating MFA requirement...');

    try {
      await apiRequest({
        url: `/ui/admin/users/${userId}/mfa-requirement`,
        method: 'PUT',
        data: { require_mfa: require }
      });

      setUserManagementMessage(
        require
          ? 'MFA requirement enabled. User will be prompted to set up MFA on next login.'
          : 'MFA requirement removed.'
      );
      fetchUsers(); // Refresh user list
    } catch (error: any) {
      console.error('Error updating MFA requirement:', error);
      const detail = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to update MFA requirement.';
      setUserManagementMessage(`Error: ${detail}`);
    }
  };

  const handleDisableMFA = async (userId: string) => {
    if (!window.confirm('Are you sure you want to completely disable MFA for this user? They will need to set it up again if required.')) {
      return;
    }

    setUserManagementMessage('Disabling MFA...');

    try {
      await apiRequest({
        url: `/ui/admin/users/${userId}/disable-mfa`,
        method: 'POST'
      });

      setUserManagementMessage('MFA has been completely disabled for the user.');
      fetchUsers(); // Refresh user list
    } catch (error: any) {
      console.error('Error disabling MFA:', error);
      const detail = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to disable MFA.';
      setUserManagementMessage(`Error: ${detail}`);
    }
  };

  return (
    <>
      {/* User Management Section */}
      <PageHeader
        title="User Management"
        actions={
          <div className="um-header-actions">
            <Button variant="secondary" onClick={fetchUsers} disabled={isLoadingUsers}>
              {isLoadingUsers ? 'Refreshing Users...' : 'Refresh Users'}
            </Button>
            <Button variant="primary" onClick={handleOpenAddUserModal} disabled={isLoadingUsers}>
              Invite User
            </Button>
          </div>
        }
      />

      <Card className="um-card">
        {isLoadingUsers && <p>Loading users...</p>}
        {userManagementMessage && (
          <div className="um-status-message">{userManagementMessage}</div>
        )}

        {!isLoadingUsers && users.length > 0 && (
          <table className="user-table">
            <thead>
              <tr>
                <th>Email (ID)</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Job Title</th>
                <th>Phone</th>
                <th>Roles</th>
                <th>Status</th>
                <th>MFA Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>{user.email}</td>
                  <td>{user.firstName || '-'}</td>
                  <td>{user.lastName || '-'}</td>
                  <td>{user.job_title || '-'}</td>
                  <td>{user.phoneNumber || '-'}</td>
                  <td>{formatRoleList(user.roles)}</td>
                  <td>
                    {user.is_active ? (
                      <span className="um-status-active">Active</span>
                    ) : (
                      <span className="um-status-pending">Pending</span>
                    )}
                  </td>
                  <td>
                    {user.mfa_required ? (
                      user.mfa_enabled ? (
                        <span className="um-mfa-enabled">Enabled</span>
                      ) : (
                        <span className="um-mfa-missing">Required (Not Set)</span>
                      )
                    ) : (
                      <span className="um-mfa-none">Not Required</span>
                    )}
                  </td>
                  <td>
                    {user.last_login ? (
                      new Date(user.last_login).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    ) : '-'}
                  </td>
                  <td>
                    <div className="um-row-actions">
                      {!user.is_active && (
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => handleResendInvitation(user.id)}
                          title="Resend invitation email"
                        >
                          Resend Invite
                        </Button>
                      )}
                      {user.is_active && (
                        <>
                          {!user.mfa_required && (
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => handleToggleMFARequirement(user.id, true)}
                              title="Require MFA for this user"
                            >
                              Require MFA
                            </Button>
                          )}
                          {user.mfa_required && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleToggleMFARequirement(user.id, false)}
                              title="Remove MFA requirement (user can still use MFA if enabled)"
                            >
                              Remove Requirement
                            </Button>
                          )}
                          {user.mfa_enabled && (
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => handleDisableMFA(user.id)}
                              title="Completely disable MFA for this user"
                            >
                              Disable MFA
                            </Button>
                          )}
                        </>
                      )}
                      <Button size="sm" variant="secondary" onClick={() => handleEditUser(user.id)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => handleDeleteUser(user.id)}>
                        Remove
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="modal">
          <div className="modal-content">
            <span className="close-button" onClick={handleCloseAddUserModal}>&times;</span>
            <h3>Invite New User</h3>
            <form onSubmit={handleSubmitAddUser}>
              <div className="form-group">
                <label htmlFor="newUserEmail">Email*:</label>
                <input
                  type="email"
                  id="newUserEmail"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="newUserFirstName">First Name:</label>
                <input
                  type="text"
                  id="newUserFirstName"
                  value={newUserFirstName}
                  onChange={(e) => setNewUserFirstName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="newUserLastName">Last Name:</label>
                <input
                  type="text"
                  id="newUserLastName"
                  value={newUserLastName}
                  onChange={(e) => setNewUserLastName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Roles*:</label>
                <div className="checkbox-group">
                  {ROLE_OPTIONS.map(option => (
                    <label key={option.key} className="checkbox-label">
                      <input
                        type="checkbox"
                        name="userRole"
                        value={option.key}
                        checked={newUserRoles.includes(option.key) || option.locked === true}
                        disabled={option.locked}
                        onChange={() => handleToggleNewRole(option.key)}
                      />
                      <span>{option.label}</span>
                      {option.description && (
                        <small className="um-role-desc">{option.description}</small>
                      )}
                    </label>
                  ))}
                </div>
              </div>
              {addUserModalMessage && <p className="modal-message">{addUserModalMessage}</p>}
              <div className="modal-actions">
                <Button type="submit" variant="primary">Send Invitation</Button>
                <Button type="button" variant="danger" onClick={handleCloseAddUserModal}>Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && editingUser && (
        <div className="modal">
          <div className="modal-content">
            <span className="close-button" onClick={handleCloseEditUserModal}>&times;</span>
            <h3>Edit User: {editingUser.email}</h3>
            <form onSubmit={handleSubmitEditUser}>
              <div className="form-group">
                <label htmlFor="editUserFirstName">First Name:</label>
                <input
                  type="text"
                  id="editUserFirstName"
                  value={editUserFirstName}
                  onChange={(e) => setEditUserFirstName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="editUserLastName">Last Name:</label>
                <input
                  type="text"
                  id="editUserLastName"
                  value={editUserLastName}
                  onChange={(e) => setEditUserLastName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="editUserTitle">Title:</label>
                <input
                  type="text"
                  id="editUserTitle"
                  value={editUserTitle}
                  onChange={(e) => setEditUserTitle(e.target.value)}
                  placeholder="e.g. Senior Claims Manager"
                />
              </div>
              <div className="form-group">
                <label htmlFor="editUserPhoneNumber">Phone Number:</label>
                <input
                  type="text"
                  id="editUserPhoneNumber"
                  value={editUserPhoneNumber}
                  onChange={(e) => setEditUserPhoneNumber(e.target.value)}
                  placeholder="e.g. 07123 456789"
                />
              </div>
              <div className="form-group">
                <label>Roles*:</label>
                <div className="checkbox-group">
                  {ROLE_OPTIONS.map(option => (
                    <label key={option.key} className="checkbox-label">
                      <input
                        type="checkbox"
                        name="editUserRole"
                        value={option.key}
                        checked={editUserRoles.includes(option.key) || option.locked === true}
                        disabled={option.locked}
                        onChange={() => handleToggleEditRole(option.key)}
                      />
                      <span>{option.label}</span>
                      {option.description && (
                        <small className="um-role-desc">{option.description}</small>
                      )}
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="editUserExcludeFromProductivity" className="um-checkbox-label-flex">
                  <input
                    type="checkbox"
                    id="editUserExcludeFromProductivity"
                    checked={editUserExcludeFromProductivity}
                    onChange={(e) => setEditUserExcludeFromProductivity(e.target.checked)}
                    className="um-checkbox-lg"
                  />
                  Exclude from productivity tracking
                </label>
                <small className="um-help-text">
                  When checked, this user will not appear in the productivity dashboard
                </small>
              </div>
              <div className="form-group">
                <label htmlFor="editUserTaskAssignable" className="um-checkbox-label-flex">
                  <input
                    type="checkbox"
                    id="editUserTaskAssignable"
                    checked={editUserTaskAssignable}
                    onChange={(e) => setEditUserTaskAssignable(e.target.checked)}
                    className="um-checkbox-lg"
                  />
                  Can be assigned tasks
                </label>
                <small className="um-help-text">
                  When checked, this user appears in the team roster used to assign tasks
                </small>
                {editUserTaskAssignable && (
                  <input
                    type="text"
                    id="editUserAssignableName"
                    value={editUserAssignableName}
                    onChange={(e) => setEditUserAssignableName(e.target.value)}
                    placeholder={`Display name (defaults to "${editUserFirstName || 'First Name'}")`}
                    className="um-assignable-name-input"
                  />
                )}
              </div>
              {editUserModalMessage && <p className="modal-message">{editUserModalMessage}</p>}
              <div className="modal-actions">
                <Button type="submit" variant="primary">Update User</Button>
                <Button type="button" variant="danger" onClick={handleCloseEditUserModal}>Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteUserModal && deletingUser && (
        <div className="modal">
          <div className="modal-content">
            <span className="close-button" onClick={handleCloseDeleteUserModal}>&times;</span>
            <h3>Confirm User Deletion</h3>
            <p>Are you sure you want to delete the following user?</p>
            <div className="um-delete-info">
              <strong>Email:</strong> {deletingUser.email}<br/>
              <strong>Name:</strong> {deletingUser.firstName} {deletingUser.lastName}<br/>
              <strong>Roles:</strong> {formatRoleList(deletingUser.roles)}
            </div>
            <p className="um-delete-warning">This action cannot be undone.</p>
            {deleteUserModalMessage && <p className="modal-message">{deleteUserModalMessage}</p>}
            <div className="modal-actions">
              <Button variant="danger" onClick={handleConfirmDeleteUser}>Delete User</Button>
              <Button variant="danger" onClick={handleCloseDeleteUserModal}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserManagement;
